import { useEffect, useState } from "react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Users, Trees, AlertTriangle, Plus, Trash2, Power, Loader2, Image as ImageIcon, Pencil, FileSpreadsheet } from "lucide-react";

const DAYS = [
  { v: 0, l: "Dim" }, { v: 1, l: "Lun" }, { v: 2, l: "Mar" }, { v: 3, l: "Mer" },
  { v: 4, l: "Jeu" }, { v: 5, l: "Ven" }, { v: 6, l: "Sam" },
];

const STATUSES = [
  { v: "non-lu", l: "Non lu", c: "bg-red-500/15 text-red-600 dark:text-red-400" },
  { v: "en-attente", l: "En attente", c: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  { v: "rapporte", l: "Rapporté", c: "bg-blue-500/15 text-blue-600 dark:text-blue-400" },
  { v: "regle", l: "Réglé", c: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
];

export default function AdminPage() {
  return (
    <Layout>
      <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mb-1">Administration</h1>
      <p className="text-muted-foreground mb-6">Gérez les usagers, les parcs et les problèmes rapportés.</p>
      <Tabs defaultValue="users">
        <TabsList className="mb-6">
          <TabsTrigger value="users" data-testid="tab-users" className="gap-2"><Users className="h-4 w-4" /> Usagers</TabsTrigger>
          <TabsTrigger value="parks" data-testid="tab-parks" className="gap-2"><Trees className="h-4 w-4" /> Parcs</TabsTrigger>
          <TabsTrigger value="problems" data-testid="tab-problems" className="gap-2"><AlertTriangle className="h-4 w-4" /> Problèmes</TabsTrigger>
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="parks"><ParksTab /></TabsContent>
        <TabsContent value="problems"><ProblemsTab /></TabsContent>
      </Tabs>
    </Layout>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "", confirm: "", role: "membre" });
  const [loading, setLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", email: "", role: "membre" });
  const [editSaving, setEditSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/users"); setUsers(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.password || form.password !== form.confirm) {
      return toast.error("Les mots de passe ne correspondent pas.");
    }
    setLoading(true);
    try {
      const { confirm, ...payload } = form;
      await api.post("/users", payload);
      toast.success("Usager créé.");
      setOpen(false);
      setForm({ first_name: "", last_name: "", email: "", password: "", confirm: "", role: "membre" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setEditForm({ first_name: u.first_name, last_name: u.last_name, email: u.email, role: u.role });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setEditSaving(true);
    try {
      await api.patch(`/users/${editId}`, editForm);
      toast.success("Usager mis à jour.");
      setEditOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setEditSaving(false); }
  };

  const toggleActive = async (u) => {
    try { await api.patch(`/users/${u.id}`, { active: !u.active }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (u) => {
    try { await api.delete(`/users/${u.id}`); toast.success("Usager supprimé."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn" className="active:scale-95 transition-transform"><Plus className="h-4 w-4 mr-2" /> Nouvel usager</Button>
          </DialogTrigger>
          <DialogContent data-testid="add-user-modal">
            <DialogHeader><DialogTitle className="font-heading">Nouvel usager</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Prénom</Label><Input data-testid="user-firstname-input" value={form.first_name} onChange={set("first_name")} /></div>
              <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="user-lastname-input" value={form.last_name} onChange={set("last_name")} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Courriel</Label><Input data-testid="user-email-input" type="email" value={form.email} onChange={set("email")} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Mot de passe</Label><Input data-testid="user-password-input" type="password" value={form.password} onChange={set("password")} /></div>
              <div className="space-y-1.5 col-span-2"><Label>Confirmer le mot de passe</Label><Input data-testid="user-confirm-password-input" type="password" value={form.confirm} onChange={set("confirm")} /></div>
              <div className="space-y-1.5 col-span-2">
                <Label>Rôle</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="membre">Membre</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button>
              <Button data-testid="user-save-btn" onClick={create} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Créer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead><TableHead>Courriel</TableHead><TableHead>Rôle</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                <TableCell className="font-medium">{u.first_name} {u.last_name}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell><Badge variant={u.role === "admin" ? "default" : "secondary"} className="capitalize">{u.role}</Badge></TableCell>
                <TableCell>{u.active ? <span className="text-emerald-600 dark:text-emerald-400 text-sm">Actif</span> : <span className="text-muted-foreground text-sm">Désactivé</span>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button data-testid={`edit-user-${u.id}`} variant="ghost" size="icon" onClick={() => openEdit(u)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`toggle-user-${u.id}`} variant="ghost" size="icon" onClick={() => toggleActive(u)} title="Activer / Désactiver"><Power className="h-4 w-4" /></Button>
                  <Button data-testid={`delete-user-${u.id}`} variant="ghost" size="icon" onClick={() => remove(u)} className="text-destructive" title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {users.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-10">Aucun usager.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent data-testid="edit-user-modal">
          <DialogHeader><DialogTitle className="font-heading">Modifier l'usager</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5"><Label>Prénom</Label><Input data-testid="edit-user-firstname-input" value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="edit-user-lastname-input" value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2"><Label>Courriel</Label><Input data-testid="edit-user-email-input" type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} /></div>
            <div className="space-y-1.5 col-span-2">
              <Label>Rôle</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="membre">Membre</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setEditOpen(false)}>Annuler</Button>
            <Button data-testid="edit-user-save-btn" onClick={saveEdit} disabled={editSaving}>{editSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ParksTab() {
  const year = new Date().getFullYear();
  const emptyForm = { name: "", address: "", visits_per_week: 1, days: [], start_date: `${year}-01-01`, end_date: `${year}-12-31` };
  const [parks, setParks] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameForm, setRenameForm] = useState({ name_before: "", name_after: "" });
  const [renaming, setRenaming] = useState(false);

  const doRename = async () => {
    if (!renameForm.name_before.trim() || !renameForm.name_after.trim()) {
      return toast.error("Les deux champs sont requis.");
    }
    setRenaming(true);
    try {
      const { data } = await api.post("/maintenances/rename", renameForm);
      toast.success(`${data.modified} entretien(s) renommé(s).`);
      setRenameOpen(false);
      setRenameForm({ name_before: "", name_after: "" });
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRenaming(false); }
  };

  const load = async () => {
    try { const { data } = await api.get("/parks"); setParks(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const toggleDay = (v) => setForm((f) => ({ ...f, days: f.days.includes(v) ? f.days.filter((d) => d !== v) : [...f.days, v] }));

  const openCreate = () => { setEditingId(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, address: p.address || "", visits_per_week: p.visits_per_week ?? 0,
      days: p.days || [], start_date: p.start_date || `${year}-01-01`, end_date: p.end_date || `${year}-12-31`,
    });
    setOpen(true);
  };
  const onDialogChange = (o) => { setOpen(o); if (!o) { setEditingId(null); setForm(emptyForm); } };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Le nom est requis.");
    if (form.start_date > form.end_date) return toast.error("La date de début doit précéder la date de fin.");
    setLoading(true);
    try {
      const payload = { ...form, visits_per_week: Number(form.visits_per_week) };
      if (editingId) {
        await api.patch(`/parks/${editingId}`, payload);
        toast.success("Parc mis à jour.");
      } else {
        await api.post("/parks", payload);
        toast.success("Parc ajouté.");
      }
      onDialogChange(false);
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  const toggleActive = async (p) => {
    try { await api.patch(`/parks/${p.id}`, { active: !p.active }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const remove = async (p) => {
    try { await api.delete(`/parks/${p.id}`); toast.success("Parc supprimé."); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end gap-2">
        <Button data-testid="rename-maintenances-btn" onClick={() => setRenameOpen(true)} variant="outline" className="active:scale-95 transition-transform"><Pencil className="h-4 w-4 mr-2" /> Renommer entretiens</Button>
        <Button data-testid="add-park-btn" onClick={openCreate} className="active:scale-95 transition-transform"><Plus className="h-4 w-4 mr-2" /> Nouveau parc</Button>
        <Dialog open={open} onOpenChange={onDialogChange}>
          <DialogContent data-testid="add-park-modal">
            <DialogHeader><DialogTitle className="font-heading">{editingId ? "Modifier le parc" : "Nouveau parc"}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Nom</Label><Input data-testid="park-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Adresse</Label><Input data-testid="park-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></div>
              <div className="space-y-1.5"><Label>Nombre de visites par semaine</Label><Input data-testid="park-visits-input" type="number" min={0} value={form.visits_per_week} onChange={(e) => setForm({ ...form, visits_per_week: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Date de début</Label><Input data-testid="park-start-input" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Date de fin</Label><Input data-testid="park-end-input" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
              </div>
              <div className="space-y-2">
                <Label>Jours d'entretien par défaut</Label>
                <div className="flex flex-wrap gap-3">
                  {DAYS.map((d) => (
                    <label key={d.v} className="flex items-center gap-1.5 cursor-pointer">
                      <Checkbox data-testid={`park-day-${d.v}`} checked={form.days.includes(d.v)} onCheckedChange={() => toggleDay(d.v)} />
                      <span className="text-sm">{d.l}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onDialogChange(false)}>Annuler</Button>
              <Button data-testid="park-save-btn" onClick={save} disabled={loading}>{loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{editingId ? "Enregistrer" : "Ajouter"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent data-testid="rename-maintenances-modal">
          <DialogHeader><DialogTitle className="font-heading">Renommer les entretiens</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-2">Tous les entretiens portant le « nom avant » seront renommés avec le « nom après ».</p>
          <div className="space-y-4">
            <div className="space-y-1.5"><Label>Nom avant</Label><Input data-testid="rename-before-input" value={renameForm.name_before} onChange={(e) => setRenameForm({ ...renameForm, name_before: e.target.value })} placeholder="Nom actuel exact" /></div>
            <div className="space-y-1.5"><Label>Nom après</Label><Input data-testid="rename-after-input" value={renameForm.name_after} onChange={(e) => setRenameForm({ ...renameForm, name_after: e.target.value })} placeholder="Nouveau nom" /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setRenameOpen(false)}>Annuler</Button>
            <Button data-testid="rename-confirm-btn" onClick={doRename} disabled={renaming}>{renaming && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Renommer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-2xl border border-border bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead><TableHead>Adresse</TableHead><TableHead>Visites / sem.</TableHead><TableHead>Jours</TableHead><TableHead>Période</TableHead><TableHead>Statut</TableHead><TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {parks.map((p) => (
              <TableRow key={p.id} data-testid={`park-row-${p.id}`}>
                <TableCell className="font-medium">{p.name}</TableCell>
                <TableCell className="text-muted-foreground">{p.address}</TableCell>
                <TableCell>{p.visits_per_week}</TableCell>
                <TableCell>{p.days.map((d) => DAYS.find((x) => x.v === d)?.l).join(", ")}</TableCell>
                <TableCell className="text-muted-foreground text-sm whitespace-nowrap">{p.start_date} → {p.end_date}</TableCell>
                <TableCell>{p.active ? <span className="text-emerald-600 dark:text-emerald-400 text-sm">Actif</span> : <span className="text-muted-foreground text-sm">Désactivé</span>}</TableCell>
                <TableCell className="text-right space-x-1">
                  <Button data-testid={`edit-park-${p.id}`} variant="ghost" size="icon" onClick={() => openEdit(p)} title="Modifier"><Pencil className="h-4 w-4" /></Button>
                  <Button data-testid={`toggle-park-${p.id}`} variant="ghost" size="icon" onClick={() => toggleActive(p)} title="Activer / Désactiver"><Power className="h-4 w-4" /></Button>
                  <Button data-testid={`delete-park-${p.id}`} variant="ghost" size="icon" onClick={() => remove(p)} className="text-destructive" title="Supprimer"><Trash2 className="h-4 w-4" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {parks.length === 0 && <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-10">Aucun parc.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ProblemsTab() {
  const [problems, setProblems] = useState([]);
  const [viewer, setViewer] = useState(null);
  const [filter, setFilter] = useState("active");
  const [exporting, setExporting] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/problems"); setProblems(data); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (p, status) => {
    try { await api.patch(`/problems/${p.id}/status`, { status }); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const exportExcel = async () => {
    setExporting(true);
    try {
      await downloadFile("/problems/export", "problemes_actifs.xlsx");
      toast.success("Export Excel généré.");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setExporting(false); }
  };

  const visible = filter === "active" ? problems.filter((p) => p.status !== "regle") : problems;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <ToggleGroup type="single" value={filter} onValueChange={(v) => v && setFilter(v)} className="justify-start">
          <ToggleGroupItem value="active" data-testid="filter-active-problems" className="px-3">Actifs seulement</ToggleGroupItem>
          <ToggleGroupItem value="all" data-testid="filter-all-problems" className="px-3">Tous</ToggleGroupItem>
        </ToggleGroup>
        <Button data-testid="export-problems-btn" onClick={exportExcel} disabled={exporting} variant="secondary" className="active:scale-95 transition-transform">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileSpreadsheet className="h-4 w-4 mr-2" />}
          Exporter vers Excel
        </Button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {visible.map((p) => {
          const st = STATUSES.find((s) => s.v === p.status);
          return (
            <div key={p.id} data-testid={`problem-card-${p.id}`} className="rounded-2xl border border-border bg-card p-4 space-y-3 animate-in fade-in slide-in-from-bottom-2 duration-300">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-heading font-semibold leading-tight">{p.title}</h3>
                <Badge className={`border-0 shrink-0 ${st?.c}`}>{st?.l}</Badge>
              </div>
              <p className="text-sm text-muted-foreground line-clamp-3">{p.description || "—"}</p>
              <div className="text-xs text-muted-foreground space-y-0.5">
                {p.park_name && <div>Parc : {p.park_name}</div>}
                {p.reported_by_name && <div>Par : {p.reported_by_name}</div>}
              </div>
              {p.photos?.length > 0 && (
                <button onClick={() => setViewer(p)} className="flex items-center gap-1.5 text-sm text-primary font-medium" data-testid={`view-photos-${p.id}`}>
                  <ImageIcon className="h-4 w-4" /> {p.photos.length} photo(s)
                </button>
              )}
              <Select value={p.status} onValueChange={(v) => setStatus(p, v)}>
                <SelectTrigger data-testid={`problem-status-select-${p.id}`} className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          );
        })}
        {visible.length === 0 && (
          <div className="col-span-full text-center text-muted-foreground py-16">Aucun problème à afficher.</div>
        )}
      </div>

      <Dialog open={!!viewer} onOpenChange={(o) => !o && setViewer(null)}>
        <DialogContent className="sm:max-w-lg" data-testid="problem-photos-modal">
          <DialogHeader><DialogTitle className="font-heading">{viewer?.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 max-h-[60vh] overflow-y-auto">
            {viewer?.photos?.map((ph, i) => (
              <img key={i} src={ph} alt="" className="rounded-lg border border-border w-full object-cover" />
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
